<script setup lang="ts">
import { onMounted, ref } from 'vue';
import A11yIcon from '@assets/icons/a11y.svg';
import AnchorIcon from '@assets/icons/anchor.svg';
import CheckIcon from '@assets/icons/check-bold.svg';
import CloseIcon from '@assets/icons/close.svg';
import ContrastHighIcon from '@assets/icons/contrast-high.svg';
import DarkIcon from '@assets/icons/dark.svg';
import GrayscaleIcon from '@assets/icons/grayscale.svg';
import LightIcon from '@assets/icons/light.svg';
import MinusIcon from '@assets/icons/minus.svg';
import PlusIcon from '@assets/icons/plus.svg';
import ResetIcon from '@assets/icons/reset.svg';
import SpeakIcon from '@assets/icons/speak.svg';
import SpeechIcon from '@assets/icons/speech.svg';
import { Theme, useA11yStore } from '@scripts/stores/a11yStore.js';
import {
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuPortal,
    DropdownMenuRoot,
    DropdownMenuTrigger,
} from 'reka-ui';

withDefaults(
    defineProps<{
        simpleLanguage?: string;
        simpleLanguageTarget?: string;
        reportIssueUrl?: string;
        reportIssueTitle?: string;
    }>(),
    {
        simpleLanguageTarget: '_self',
        simpleLanguage: undefined,
        reportIssueUrl: undefined,
        reportIssueTitle: undefined,
    }
);

const outer = ref(null);

const a11yStore = useA11yStore();

const itemClass =
    'flex items-center gap-5 text-primary hover:bg-primary hover:text-primary-foreground active:bg-black active:text-white focusable py-5 px-15 text-sm font-bold cursor-pointer';
</script>

<template>
    <DropdownMenuRoot :modal="false">
        <DropdownMenuTrigger
            class="group hover:bg-primary hover:text-primary-foreground relative top-0 left-0 flex size-24 items-center justify-center focus:outline-0 active:bg-black active:text-white data-[state=open]:shadow"
            :aria-label="$t('Show Accessibility Settings')"
        >
            <div
                class="focusable relative z-30 rounded-full sm:size-12 lg:size-20"
            >
                <A11yIcon
                    class="absolute top-1/2 left-1/2 size-8 -translate-1/2 group-data-[state=open]:hidden sm:size-auto"
                />
                <CloseIcon
                    class="absolute top-1/2 left-1/2 hidden size-8 -translate-1/2 group-data-[state=open]:block sm:size-auto"
                />
            </div>
        </DropdownMenuTrigger>
        <DropdownMenuPortal>
            <DropdownMenuContent
                class="bg-background text-foreground isolate z-50 w-96 rounded-br shadow"
            >
                <DropdownMenuItem
                    v-if="simpleLanguage !== undefined"
                    :class="itemClass"
                    as-child
                >
                    <a
                        :href="simpleLanguage"
                        :target="simpleLanguageTarget"
                    >
                        <SpeechIcon />
                        {{ $t('Simple Language') }}
                    </a>
                </DropdownMenuItem>
                <DropdownMenuItem
                    :class="itemClass"
                    @select.prevent="a11yStore.fontSizeMultiplier += 0.2"
                >
                    <PlusIcon />
                    {{ $t('Increase Font') }}
                </DropdownMenuItem>
                <DropdownMenuItem
                    :class="itemClass"
                    @select.prevent="a11yStore.fontSizeMultiplier -= 0.2"
                >
                    <MinusIcon />
                    {{ $t('Decrease Font') }}
                </DropdownMenuItem>
                <DropdownMenuItem
                    :class="itemClass"
                    @select.prevent="a11yStore.grayscale = !a11yStore.grayscale"
                >
                    <GrayscaleIcon />
                    <span class="flex-1">{{ $t('Grayscale') }}</span>
                    <CheckIcon
                        v-if="a11yStore.grayscale"
                        class="-mr-6"
                    />
                </DropdownMenuItem>
                <DropdownMenuItem
                    :class="itemClass"
                    @select.prevent="
                        a11yStore.highContrast = !a11yStore.highContrast
                    "
                >
                    <ContrastHighIcon />
                    <span class="flex-1">{{ $t('High Contrast') }}</span>
                    <CheckIcon
                        v-if="a11yStore.highContrast"
                        class="-mr-6"
                    />
                </DropdownMenuItem>
                <DropdownMenuItem
                    :class="itemClass"
                    @select.prevent="a11yStore.theme = Theme.Light"
                >
                    <LightIcon />
                    <span class="flex-1">{{ $t('Light Background') }}</span>
                    <CheckIcon
                        v-if="a11yStore.theme === Theme.Light"
                        class="-mr-6"
                    />
                </DropdownMenuItem>
                <DropdownMenuItem
                    :class="itemClass"
                    @select.prevent="a11yStore.theme = Theme.Dark"
                >
                    <DarkIcon />
                    <span class="flex-1">{{ $t('Dark Background') }}</span>
                    <CheckIcon
                        v-if="a11yStore.theme === Theme.Dark"
                        class="-mr-6"
                    />
                </DropdownMenuItem>
                <DropdownMenuItem
                    :class="itemClass"
                    @select.prevent="
                        a11yStore.underlineLinks = !a11yStore.underlineLinks
                    "
                >
                    <AnchorIcon />
                    <span class="flex-1">{{ $t('Underline Links') }}</span>
                    <CheckIcon
                        v-if="a11yStore.underlineLinks"
                        class="-mr-6"
                    />
                </DropdownMenuItem>
                <DropdownMenuItem
                    v-if="
                        reportIssueUrl !== undefined &&
                        reportIssueTitle !== undefined
                    "
                    :class="itemClass"
                    as-child
                >
                    <a :href="reportIssueUrl">
                        <SpeakIcon />
                        {{ reportIssueTitle }}
                    </a>
                </DropdownMenuItem>
                <DropdownMenuItem
                    :class="[itemClass, 'hover:rounded-br']"
                    @select.prevent="a11yStore.reset()"
                >
                    <ResetIcon />
                    {{ $t('Reset all') }}
                </DropdownMenuItem>
            </DropdownMenuContent>
        </DropdownMenuPortal>
    </DropdownMenuRoot>
</template>

<style scoped>
/* fix shadow overlay */
button[data-state='open'] {
    &::before {
        content: '';
        position: absolute;
        inset: 0;
        z-index: 20;
        background-color: var(--color-background);
        pointer-events: none;
    }

    &:hover::before {
        background-color: var(--color-primary);
    }
}
</style>
